import { MessageDiv } from './styles';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { isEmpty } from '../../../helpers/common';
import { EMPTY_STATE_SIZE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../global/constants';
import Modal from '../../modals/modal';
import AddGroupMemberModal from '../../modals/addGroupMemberModal';
import DeleteModal from '../../modals/deleteModal';
import ConfirmationModal from '../../modals/confirmationModal';
import { useMobileDevice } from '../../../global/useMobile';
import MessagesFeed from '../messageFeed';
import { rootStore } from '../../../mobx/rootStore';
import IconButton from '../../button/iconButton';
import BottomNavigationItem from '../../bottomNavigation/subNavigation/bottomNavigationItem';
import { observer } from 'mobx-react-lite';
import EmptyStateV2 from '../../emptyState';
import SubNavigation from '../../bottomNavigation/subNavigation';
import ChatGroupDropdown from './chatGroupDropdown';
import { useGroupModals } from './hooks/useGroupModals';
import { useGroupActions } from './hooks/useGroupActions';
import MessageDetailHeader from './MessageDetailHeader';
import { openChatGroupMessages } from '../../../services/conversationSyncService';

interface Props {
  newGroup: boolean;
  loadData: () => void;
  onCloseNewGroup: () => void;
  loading: boolean;
  onClickNewChat: () => void;
  attachment?: boolean;
  setAttachment?: (value: boolean) => void;
}

const MessageDetail = ({
  newGroup,
  loadData,
  onCloseNewGroup,
  loading,
  onClickNewChat,
  attachment,
  setAttachment
}: Props) => {
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // mobx store variable
  const { chatMessageStore, workspaceStore, currentUser, projectStore } = rootStore;
  const { currentChatGroup, chatGroups } = chatMessageStore;
  const { messages } = currentChatGroup || {};
  const { currentWorkspace } = workspaceStore;
  const { currentProject } = projectStore;
  // States variable
  const [pageScrolled, setPageScrolled] = useState<boolean>(false);
  const [selectFile, setSelectFile] = useState<boolean>(false);
  // Custom Hooks
  const { modals, removeUserId, open, close } = useGroupModals();
  const { actions, loading: actionsLoading } = useGroupActions({
    loadData,
    close,
    removeUserId
  });
  // Other variables
  const mobile = useMobileDevice();

  const scrollToBottom = () => {
    setPageScrolled(true);
    messagesEndRef.current?.scrollIntoView({
      inline: 'nearest',
      block: 'nearest'
    });
  };

  useEffect(() => {
    if (!pageScrolled && messages?.length && messages?.length > 0) scrollToBottom();
  }, [messages, pageScrolled]);

  useEffect(() => {
    setPageScrolled(false);
  }, [currentChatGroup?.id]);

  // Sidebar groups are lean (no messages), so load the opened conversation's messages.
  useEffect(() => {
    if (currentChatGroup?.id && !newGroup) {
      openChatGroupMessages(currentChatGroup.id, currentChatGroup.type);
    }
    // Runs per conversation id only; `type` is fixed for a given id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChatGroup?.id, newGroup]);

  const handleMenuItemClick = useCallback(
    (key: number | string) => {
      if (key === 1) {
        actions.handlePinChatGroup();
      } else if (key === 2) {
        open('clearMessage');
      }
    },
    [actions, open]
  );

  const handleAttachment = useCallback(
    (value: boolean) => {
      setAttachment && setAttachment(value);
      rootStore.projectStore.setCurrentProject(currentChatGroup?.id || '');
    },
    [currentChatGroup?.id, setAttachment]
  );

  const navbarEnvironment = useMemo(
    () => ({ isNavbarMessage: true, isProjectDetail: false, isProjectMessageDetail: false, isMessage: true }),
    []
  );

  const navbarFileSelection = useMemo(
    () => ({
      selectFile,
      setSelectFile: (value: boolean) => setSelectFile(value),
      attachment: !!attachment,
      setAttachment: (value: boolean) => handleAttachment(value)
    }),
    [selectFile, attachment, handleAttachment]
  );

  const renderMoreIcon = useCallback(() => {
    return mobile ? (
      <BottomNavigationItem iconName='three-dots-horizontal-icon' />
    ) : (
      <IconButton
        iconName='three-dots-horizontal-icon'
        size={ICON_BUTTON_SIZE.MEDIUM}
        variant={ICON_BUTTON_TYPE.TERTIARY}
      />
    );
  }, [mobile]);

  return (
    <>
      <MessageDiv>
        {/* Header */}
        {(newGroup || !isEmpty(currentChatGroup)) && (
          <MessageDetailHeader
            selectFile={selectFile}
            handleMenuItemClick={handleMenuItemClick}
            newGroup={newGroup}
            loadData={loadData}
            onCloseNewGroup={onCloseNewGroup}
            loading={loading}
            open={open}
            actions={actions}
          />
        )}
        {/* Empty State */}
        {chatGroups?.length === 0 && isEmpty(currentChatGroup) && !loading && (
          <EmptyStateV2
            size={EMPTY_STATE_SIZE.MEDIUM}
            title='No messages yet'
            description='You have no messages at the moment'
            buttonTitle='New message'
            imageUrl={'message_empty'}
            onClick={onClickNewChat}
            isAlignedCenter={true}
          />
        )}
        {/* Chat Group Messages */}
        {!isEmpty(currentChatGroup) && (
          <MessagesFeed
            loading={loading || (!newGroup && chatMessageStore.isOpenChatLoading)}
            messages={messages || []}
            loadData={loadData}
            currentProject={currentChatGroup}
            projectAllFiles={currentProject?.files || []}
            isMessageLoading
            newGroup={newGroup}
            environment={navbarEnvironment}
            fileSelection={navbarFileSelection}
            rightElement={
              !newGroup && (
                <ChatGroupDropdown
                  chatGroupType={currentChatGroup?.type}
                  currentChatGroup={currentChatGroup}
                  currentUser={currentUser}
                  currentWorkspace={currentWorkspace}
                  onPinChatGroup={actions.handlePinChatGroup}
                  onOpenEditGroupModal={() => {
                    if (currentChatGroup) chatMessageStore.setCurrentChatGroupName(currentChatGroup);
                    open('editGroup');
                  }}
                  onOpenClearMessageConfirmation={() => open('clearMessage')}
                  onRemoveMember={(userId: string) => open('alert', userId)}
                  onMenuItemClick={handleMenuItemClick}
                  renderMoreIcon={renderMoreIcon}
                />
              )
            }
          />
        )}
      </MessageDiv>

      {newGroup && <SubNavigation backNavigationPath='/messages' />}

      {/* Modals */}
      <Modal open={modals.member} onClose={() => close('member')} maxWidth={'400px'}>
        <AddGroupMemberModal
          onClose={() => close('member')}
          heading={'Add member'}
          label={'Member Name'}
          usersList={currentWorkspace?.users}
          groupUserList={currentChatGroup?.users}
          onAddMembers={actions.onAddMembers}
          loading={actionsLoading.submit}
        />
      </Modal>
      <Modal open={modals.editGroup} onClose={() => close('editGroup')} maxWidth={'400px'}>
        <AddGroupMemberModal
          onClose={() => close('editGroup')}
          heading={'Edit Group name'}
          label={'Group Name'}
          isEditGroupModal={true}
          loading={actionsLoading.update}
          onChange={actions.onChangeGroupName}
          onEditGroupName={actions.onClickSave}
        />
      </Modal>
      <Modal open={modals.alert} onClose={() => close('alert')} maxWidth={'334px'}>
        <DeleteModal
          isYes={true}
          onClose={() => close('alert')}
          onOk={() => actions.onClickRemoveUser()}
          modalTitle={currentUser?.id === removeUserId ? 'Leave Group?' : 'Remove Member?'}
          description={
            currentUser?.id === removeUserId
              ? 'Are you sure you want to leave this Group?'
              : 'Are you sure you want to remove this Member?'
          }
          loading={actionsLoading.submit}
        />
      </Modal>
      <Modal open={modals.clearMessage} onClose={() => close('clearMessage')} maxWidth={'334px'}>
        <ConfirmationModal
          loading={actionsLoading.clearMessage}
          onOk={actions.onDeleteGroupMessage}
          onClose={() => close('clearMessage')}
          modalTitle='Delete group message'
          description='Are you sure you want to clear group message?'
        />
      </Modal>
    </>
  );
};

export default observer(MessageDetail);
